import { createSlice } from '@reduxjs/toolkit';
import authApi from 'interceptor/authInterceptor';
import { dispatch } from 'store';

export interface HierarchyNode {
  _id: string;
  companyName: string;
  businessNumber?: string;
  parentEntityId?: string | null;
  currency?: string;
  inheritParentSettings?: boolean;
  children?: HierarchyNode[];
}

export interface HierarchyState {
  tree: HierarchyNode | null;
  selectedCompanyId: string;
  selectedNodes: { [companyId: string]: boolean };
  loading: boolean;
  error: any;
}

const initialState: HierarchyState = {
  tree: null,
  selectedCompanyId: '',
  selectedNodes: {},
  loading: false,
  error: null
};

// Helper: Collect all nodes into a flat map and default checked state
export const flattenTree = (
  node: HierarchyNode | null,
  defaultChecked: boolean = true
): { [id: string]: boolean } => {
  const map: { [id: string]: boolean } = {};
  if (!node) return map;

  const traverse = (item: HierarchyNode, parentIsChecked: boolean) => {
    // If inheritParentSettings !== false and parent is checked, default to checked
    const isChecked = item.inheritParentSettings !== false && parentIsChecked;
    map[item._id] = isChecked;
    if (item.children && Array.isArray(item.children)) {
      item.children.forEach(child => traverse(child, isChecked));
    }
  };

  map[node._id] = true;
  if (node.children) {
    node.children.forEach(child => traverse(child, true));
  }

  return map;
};

// Helper: Extract subtree rooting at targetId
export const findSubtree = (node: HierarchyNode | null, targetId: string): HierarchyNode | null => {
  if (!node) return null;
  if (node._id === targetId) return node;
  if (node.children) {
    for (const child of node.children) {
      const found = findSubtree(child, targetId);
      if (found) return found;
    }
  }
  return null;
};

const companyHierarchySlice = createSlice({
  name: 'companyHierarchy',
  initialState,
  reducers: {
    startLoading(state) {
      state.loading = true;
      state.error = null;
    },
    hasError(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    setHierarchyTree(state, action) {
      const tree: HierarchyNode = action.payload;
      state.tree = tree;
      state.loading = false;
      state.error = null;
      if (tree) {
        state.selectedCompanyId = tree._id;
        state.selectedNodes = flattenTree(tree, true);
      }
    },
    setSelectedCompany(state, action) {
      const companyId = action.payload;
      state.selectedCompanyId = companyId;
      // When a specific company is selected as root context (e.g. A11),
      // we locate that subtree and re-scope selectedNodes
      const sub = findSubtree(state.tree, companyId);
      if (sub) {
        state.selectedNodes = flattenTree(sub, true);
      }
    },
    toggleNodeSelection(state, action) {
      const { companyId, checked, cascade } = action.payload;
      state.selectedNodes[companyId] = checked;

      // Cascading check/uncheck to all children
      if (cascade && state.tree) {
        const sub = findSubtree(state.tree, companyId);
        if (sub && sub.children) {
          const cascadeUpdate = (item: HierarchyNode, val: boolean) => {
            state.selectedNodes[item._id] = val;
            if (item.children) {
              item.children.forEach(c => cascadeUpdate(c, val));
            }
          };
          sub.children.forEach(c => cascadeUpdate(c, checked));
        }
      }
    },
    clearHierarchy(state) {
      state.tree = null;
      state.selectedCompanyId = '';
      state.selectedNodes = {};
      state.loading = false;
      state.error = null;
    }
  }
});

export const {
  startLoading,
  hasError,
  setHierarchyTree,
  setSelectedCompany,
  toggleNodeSelection,
  clearHierarchy
} = companyHierarchySlice.actions;

export default companyHierarchySlice.reducer;

/**
 * Fetch company hierarchy from V3 API
 * GET /v3/api/company/hierarchy
 */
export const fetchCompanyHierarchy = (targetCompanyId?: string) => async () => {
  try {
    dispatch(startLoading());
    const query = targetCompanyId ? `?companyId=${targetCompanyId}` : '';
    const response = await authApi.get(`/v3/api/company/hierarchy${query}`);
    if (response?.data?.data) {
      dispatch(setHierarchyTree(response.data.data));
      return response.data.data;
    }
  } catch (error: any) {
    dispatch(hasError(error?.response?.data || error.message));
  }
};

